import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext, useInputHelper } from 'framework/form';
import useCrmService from 'domain/sales/salesService';
import { SxProps } from '@mui/material';
import { OWNER_SOURCE, getFullName } from './ownerHelper';
import useAuthenticatedUserService, { UserName } from 'infrastructure/security/authentication/authenticatedUserService';
import OwnerDropdown from 'domain/sales/shared/input/OwnerDropdown';
import { EditableFieldLayout } from 'framework/ui/layout';

const OWNER_NAME = 'ownerName';

type OwnerInputProps = {
  source?: string;
  label: string;
  isUserCanChange: boolean;
  dropdownPlaceholder?: string;
  sxFormControll?: SxProps;
  helpText?: string;
};

const OwnerInput = ({
  isUserCanChange,
  label,
  dropdownPlaceholder,
  sxFormControll,
  helpText,
  ...otherProps
}: OwnerInputProps) => {
  const props = {
    source: OWNER_SOURCE,
    ...otherProps,
  };

  const { getIsRequired, getRawOnChangeCallback, initializeValue, getOnChangeCallback, getSource } =
    useInputHelper(props);

  const rawOnChangeCallback = getRawOnChangeCallback() || (() => {});

  const { getUserAlias, getUserName } = useAuthenticatedUserService();
  const { data } = useFormContext();
  const { getActiveSalesManagers } = useCrmService();
  const [userAlias, setUserAlias] = useState<Nullable<string>>(null);
  const [userName, setUserName] = useState<Nullable<UserName> | {}>({});
  const [sales, setSales] = useState([]);

  useEffect(() => {
    (async () => {
      const alias = await getUserAlias();
      setUserAlias(alias);
      const name = await getUserName();
      setUserName(name);

      try {
        const sms = await getActiveSalesManagers();
        setSales(sms);
      } catch (e) {
        setSales([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save initial lead owner
  const initialOwner = useMemo(
    () => (data && data[getSource()]) || userAlias,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userAlias],
  );

  useEffect(() => {
    if (initialOwner) {
      initializeValue(initialOwner);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOwner]);

  const onCancel = () => {
    // when user click "cancel change"
    // => it imitate onchange callback with initial owner for ownerAlias field.
    rawOnChangeCallback({
      [getSource()]: initialOwner,
    });
  };

  const fieldComponent = <b>{(data && data[OWNER_NAME]) || getFullName(userName as UserName)}</b>;

  const editFieldInput = (
    <OwnerDropdown
      sx={sxFormControll}
      label={label}
      source={getSource()}
      placeholder={dropdownPlaceholder}
      sales={sales}
      onChangeCallback={rawOnChangeCallback}
    />
  );

  return (
    <EditableFieldLayout
      fieldComponent={fieldComponent}
      editFieldInput={editFieldInput}
      isUserCanChange={isUserCanChange}
      isRequired={getIsRequired()}
      label={label}
      helpText={helpText}
      onChangeCallback={getOnChangeCallback()}
      onCancel={onCancel}
    />
  );
};

export default OwnerInput;
